import { GripVertical, Info } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface TemplateVariablesPanelProps {
  onInsertVariable: (variable: string) => void;
  /** When provided, only variable sections whose name is in this list are shown. */
  allowedSections?: string[];
}

interface Variable {
  name: string;
  variable: string;
  description: string;
  category: string;
  hidden?: boolean;
}

export const TEMPLATE_VARIABLES: Variable[] = [
  // Customer Information
  {
    name: 'Customer Name',
    variable: '<<customerName>>',
    description: 'Full name of the customer',
    category: 'Customer Information',
  },
  {
    name: 'Customer Email',
    variable: '<<customerEmail>>',
    description: 'Email address of the customer',
    category: 'Customer Information',
  },
  {
    name: 'Customer Phone',
    variable: '<<customerPhone>>',
    description: 'Phone number of the customer',
    category: 'Customer Information',
  },
  {
    name: 'Account Number',
    variable: '<<accountNumber>>',
    description: 'Customer account number',
    category: 'Customer Information',
  },
  {
    name: 'Contact First Name',
    variable: '<<contactFirstName>>',
    description: 'First name of the primary contact person',
    category: 'Customer Information',
  },
  {
    name: 'Contact Surname',
    variable: '<<contactSurname>>',
    description: 'Surname of the primary contact person',
    category: 'Customer Information',
  },
  {
    name: 'Contact Email',
    variable: '<<contactEmail>>',
    description: 'Email address of the primary contact',
    category: 'Customer Information',
  },
  {
    name: 'Contact Phone',
    variable: '<<contactPhone>>',
    description: 'Phone number of the primary contact',
    category: 'Customer Information',
  },
  {
    name: 'Account Balance',
    variable: '<<accountBalance>>',
    description:
      'Total outstanding balance (in customer currency, or base currency for multi-currency accounts)',
    category: 'Customer Information',
  },
  {
    name: 'Overdue Amount',
    variable: '<<overdueAmount>>',
    description:
      'Total overdue amount (in customer currency, or base currency for multi-currency accounts)',
    category: 'Customer Information',
  },
  {
    name: 'Credit Limit',
    variable: '<<creditLimit>>',
    description: 'Customer credit limit',
    category: 'Customer Information',
  },
  {
    name: 'Payment Terms',
    variable: '<<paymentTerms>>',
    description: 'Customer payment terms code (e.g. Net 30)',
    category: 'Customer Information',
  },
  {
    name: 'Due Days',
    variable: '<<dueDays>>',
    description: 'Number of days until payment is due',
    category: 'Customer Information',
  },
  {
    name: 'Due Days Type',
    variable: '<<dueDaysType>>',
    description:
      'How due days are calculated (e.g. Days from Invoice, End of Following Month)',
    category: 'Customer Information',
  },

  // Transaction Details
  {
    name: 'Overdue Transactions',
    variable: '<<overdueTransactions>>',
    description: 'Table of all overdue transactions on the customer account',
    category: 'Transaction Details',
  },
  {
    name: 'Outstanding Transactions',
    variable: '<<outstandingTransactions>>',
    description:
      'Table of all outstanding transactions on the customer account',
    category: 'Transaction Details',
  },

  // Collection Information
  {
    name: 'Case Amount',
    variable: '<<caseAmount>>',
    description: 'Total amount of the collection case',
    category: 'Collection Information',
  },
  {
    name: 'Days Overdue',
    variable: '<<daysOverdue>>',
    description: 'Number of days the collection case has been in collection',
    category: 'Collection Information',
  },
  {
    name: 'Case Outstanding Transactions',
    variable: '<<caseOutstandingTransactions>>',
    description:
      'Table of outstanding transactions linked to the collection case',
    category: 'Collection Information',
  },
  {
    name: 'Case Overdue Transactions',
    variable: '<<caseOverdueTransactions>>',
    description: 'Table of overdue transactions linked to the collection case',
    category: 'Collection Information',
  },

  // Company Information
  {
    name: 'Company Name',
    variable: '<<companyName>>',
    description: 'Your company name',
    category: 'Company Information',
  },
  {
    name: 'Company Email',
    variable: '<<companyEmail>>',
    description: 'Your company email address',
    category: 'Company Information',
  },
  {
    name: 'Company Phone',
    variable: '<<companyPhone>>',
    description: 'Your company phone number',
    category: 'Company Information',
  },
  {
    name: 'Support Email',
    variable: '<<supportEmail>>',
    description: 'Customer support email',
    category: 'Company Information',
    hidden: true,
  },
  {
    name: 'Credit Controller',
    variable: '<<creditController>>',
    description: 'Full name of the assigned credit controller',
    category: 'Company Information',
  },
  {
    name: 'Credit Controller First Name',
    variable: '<<creditControllerFirstName>>',
    description: 'First name of the assigned credit controller',
    category: 'Company Information',
  },
  {
    name: 'Credit Controller Last Name',
    variable: '<<creditControllerLastName>>',
    description: 'Last name of the assigned credit controller',
    category: 'Company Information',
  },
  {
    name: 'Credit Controller Email',
    variable: '<<creditControllerEmail>>',
    description: 'Email address of the credit controller',
    category: 'Company Information',
  },
  {
    name: 'Company Logo',
    variable: '<<companyLogo>>',
    description: 'Your company logo image (set in Company Settings)',
    category: 'Company Information',
  },
  {
    name: 'Bank Name',
    variable: '<<companyBankName>>',
    description: 'Company bank account name',
    category: 'Company Information',
  },
  {
    name: 'Sort Code',
    variable: '<<sortCode>>',
    description: 'Company bank sort code',
    category: 'Company Information',
  },
  {
    name: 'Bank Account Number',
    variable: '<<bankAccountNumber>>',
    description: 'Company bank account number',
    category: 'Company Information',
  },
  {
    name: 'IBAN Number',
    variable: '<<ibanNumber>>',
    description: 'Company IBAN number',
    category: 'Company Information',
  },
  {
    name: 'SWIFT/BIC Code',
    variable: '<<swiftBicCode>>',
    description: 'Company SWIFT/BIC code',
    category: 'Company Information',
  },

  // Payment Information
  {
    name: 'Promised Amount',
    variable: '<<promisedAmount>>',
    description: 'Amount promised to pay',
    category: 'Payment Information',
  },
  {
    name: 'Promise Date',
    variable: '<<promiseDate>>',
    description: 'Date promised to pay by',
    category: 'Payment Information',
  },

  // System Information
  {
    name: 'Current Date',
    variable: '<<systemDate>>',
    description: 'Current date when email is sent',
    category: 'System Information',
  },
  {
    name: 'Current Time',
    variable: '<<systemTime>>',
    description: 'Current time when email is sent',
    category: 'System Information',
  },
];

// Category color mapping
const getCategoryColor = (
  category: string,
): {
  variant: 'default' | 'secondary' | 'outline' | 'accent';
  accent?:
    | 'destructive'
    | 'success'
    | 'warning'
    | 'info'
    | 'teal'
    | 'purple'
    | 'nile'
    | 'cerulean'
    | 'lagoon'
    | 'eagle'
    | 'sap'
    | 'orange'
    | 'brick'
    | 'red'
    | 'violet'
    | 'blue-violet';
} => {
  const colorMap: Record<
    string,
    {
      variant: 'default' | 'secondary' | 'outline' | 'accent';
      accent?:
        | 'destructive'
        | 'success'
        | 'warning'
        | 'info'
        | 'teal'
        | 'purple'
        | 'nile'
        | 'cerulean'
        | 'lagoon'
        | 'eagle'
        | 'sap'
        | 'orange'
        | 'brick'
        | 'red'
        | 'violet'
        | 'blue-violet';
    }
  > = {
    'Customer Information': { variant: 'accent', accent: 'blue-violet' },
    'Transaction Details': { variant: 'accent', accent: 'teal' },
    'Company Information': { variant: 'accent', accent: 'purple' },
    'Payment Information': { variant: 'accent', accent: 'orange' },
    'System Information': { variant: 'accent', accent: 'lagoon' },
    'Collection Information': { variant: 'accent', accent: 'brick' },
  };
  return colorMap[category] || { variant: 'secondary' };
};

export function TemplateVariablesPanel({
  onInsertVariable,
  allowedSections,
}: TemplateVariablesPanelProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [isDragging, setIsDragging] = useState(false);

  // When allowedSections changes (e.g. user picks a different template category),
  // reset the local filter back to "all" so the user sees every allowed section.
  const allowedKey = allowedSections ? allowedSections.join(',') : '';
  useEffect(() => {
    setSelectedCategory('all');
  }, [allowedKey]);

  // Base set of variables – restricted to allowed sections when provided
  const allowedSectionSet = allowedSections ? new Set(allowedSections) : null;
  const visibleVariables = TEMPLATE_VARIABLES.filter((v) => !v.hidden);
  const baseVariables = allowedSectionSet
    ? visibleVariables.filter((v) => allowedSectionSet.has(v.category))
    : visibleVariables;

  // Group variables by category
  const categories = [
    'all',
    ...Array.from(new Set(baseVariables.map((v) => v.category))),
  ];

  const filteredVariables =
    selectedCategory === 'all'
      ? baseVariables
      : baseVariables.filter((v) => v.category === selectedCategory);

  const groupedVariables = filteredVariables.reduce(
    (acc, variable) => {
      if (!acc[variable.category]) {
        acc[variable.category] = [];
      }
      acc[variable.category].push(variable);
      return acc;
    },
    {} as Record<string, Variable[]>,
  );

  return (
    <TooltipProvider delayDuration={300}>
      <div className="space-y-4">
        {/* Header */}
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Click or drag to insert at cursor position
          </p>
        </div>

        {/* Category Filter */}
        <div className="space-y-2">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Filter by:
          </span>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((category) => (
                <SelectItem key={category} value={category}>
                  {category === 'all' ? 'All Variables' : category}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Variables by Category */}
        <div className="space-y-4">
          {Object.entries(groupedVariables).map(([category, variables]) => (
            <div key={category} className="space-y-1.5">
              <h3 className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">
                {category}
              </h3>
              <div className="space-y-1">
                {variables.map((variable) => {
                  const categoryStyle = getCategoryColor(variable.category);
                  return (
                    <Tooltip key={variable.variable}>
                      <TooltipTrigger asChild>
                        <button
                          draggable
                          onDragStart={(e) => {
                            setIsDragging(true);
                            e.dataTransfer.effectAllowed = 'copy';
                            // Set both data types - the custom handler will prevent default insertion
                            e.dataTransfer.setData(
                              'text/plain',
                              variable.variable,
                            );
                            e.dataTransfer.setData(
                              'application/x-template-variable',
                              variable.variable,
                            );
                            // Add visual feedback
                            e.currentTarget.style.opacity = '0.5';
                          }}
                          onDragEnd={(e) => {
                            // Reset visual feedback
                            e.currentTarget.style.opacity = '1';
                            // Reset dragging state after a short delay to prevent click from firing
                            setTimeout(() => setIsDragging(false), 100);
                          }}
                          onClick={() => {
                            // Only insert on click if not dragging
                            if (!isDragging) {
                              onInsertVariable(variable.variable);
                            }
                          }}
                          className="group relative flex items-start gap-2 px-3 py-2.5 w-full border rounded-md hover:border-primary hover:bg-accent transition-colors text-left cursor-grab active:cursor-grabbing"
                        >
                          <GripVertical className="h-4 w-4 text-muted-foreground/50 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0 space-y-1.5">
                            <span className="font-medium text-sm leading-tight block">
                              {variable.name}
                            </span>
                            <Badge
                              variant={categoryStyle.variant}
                              accent={categoryStyle.accent}
                              className="text-[10px] font-mono"
                            >
                              {variable.variable}
                            </Badge>
                          </div>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="left" className="max-w-[250px]">
                        <p className="text-xs">{variable.description}</p>
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Info Box */}
        <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-lg">
          <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-xs font-medium text-blue-900 dark:text-blue-100">
              How to use
            </p>
            <p className="text-xs text-blue-700 dark:text-blue-300 leading-snug">
              Click your desired insertion point in the template, then click a
              variable to insert it. Or drag and drop a variable directly into
              the template. Hover over any variable to see its description.
            </p>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
